/**
 * The feed's assembly, pure (plan §1 Client, the `src/lib/feed.ts` row):
 * fetch results plus a viewer plus filters in, a sorted, verdict-bearing list
 * out. `FeedScreen` just renders the output — every rule about what appears,
 * in what order, and why the list might be empty lives here, under test
 * (R5's "one place, tested, not scattered across a component").
 *
 * The three feed-level drops happen *before* any badge is computed:
 *
 * - **own ads** — the feed is for browsing other people's shifts; your ads
 *   are managed from the calendar. A feed card for your own ad has no fit
 *   badge to show and no offer to make.
 * - **same-block advertisers** (CLAUDE.md bite #7) — people on your block
 *   work the identical shifts on the identical days; showing them is noise
 *   that makes the app look broken. Absent, not greyed, not at the bottom.
 * - **past dates** — the view has no date predicate (the server's
 *   `current_date` is UTC; the app's today is ACT wall-clock), so this
 *   client-side filter with the injected `today` is what keeps yesterday's
 *   ads out of today's feed until Phase 5's `pg_cron` auto-withdraw.
 *
 * Then `fitAgainst` (src/rules/fit.ts) grades every surviving ad, and the
 * filters narrow without reordering. Sorting is by band first — clean fits,
 * then warnings, then blocked, each by date — so a §3.3-halved feed surfaces
 * its good matches (the liquidity problem this phase ships against).
 *
 * **The two empty states are different facts and must stay distinguishable**
 * (§3.3 again): `noAds` is "nobody's advertising right now" (the fetch
 * returned nothing at all), while `hiddenCount > 0` with no entries is
 * "there are ads, but none fit your week or your filters". An empty-looking
 * feed with the wrong reason is the app looking broken.
 */

import { addDays } from '../engine/civil'
import type {
  BlockCode,
  DateRange,
  DayException,
  IsoDate,
  RosterConfig,
  ShiftCode,
  ShiftInstance,
} from '../engine/types'
import { fitAgainst } from '../rules/fit'
import type { FitVerdict } from '../rules/fit'
import type { FeedItem } from './ads'

/** What the user has asked to see. */
export interface FeedFilters {
  /** Inclusive — an `IsoDate` pair, string comparison is chronological. */
  range: DateRange
  code: ShiftCode | 'ALL'
  hideWarnings: boolean
}

/** The default window: today → +8 weeks (plan §1's ruling). */
export function defaultFeedFilters(today: IsoDate): FeedFilters {
  return { range: { from: today, to: addDays(today, 56) }, code: 'ALL', hideWarnings: false }
}

/** The viewer, as the screen assembles them from the session and one fetch. */
export interface FeedViewer {
  userId: string
  block: BlockCode | null
  /** effectiveRoster() over the feed window +1 day each side. */
  shifts: readonly ShiftInstance[]
  exceptions: readonly DayException[]
}

/** One card's worth: the ad and its verdict, already computed. */
export interface FeedEntry {
  item: FeedItem
  verdict: FitVerdict
}

/** The assembled feed, with the numbers its empty states need. */
export interface FeedAssembly {
  entries: FeedEntry[]
  /** True only when the fetch itself returned nothing at all. */
  noAds: boolean
  /** How many fetched ads are not shown — hidden by fit, block or filters. */
  hiddenCount: number
}

/** Band order: clean first, warnings next, blocked last. A §3.3 feed's good matches surface. */
function bandRank(verdict: FitVerdict): number {
  if (verdict.kind === 'CLEAN') return 0
  if (verdict.kind === 'WARN') return 1
  return 2
}

/**
 * The whole feed in one pure function. `today` is an argument, never the
 * clock — the same seam every screen takes, so these fixtures run offline
 * and deterministically.
 */
export function assembleFeed(
  items: readonly FeedItem[],
  viewer: FeedViewer,
  filters: FeedFilters,
  config: RosterConfig,
  today: IsoDate,
): FeedAssembly {
  const browsable = items.filter((item) => {
    // Own ads: not hidden — managed from the calendar — but never browsed.
    if (item.ownerUserId === viewer.userId) return false
    // Same block: useless as swap partners (bite #7), absent by ruling.
    if (viewer.block !== null && item.blockCode === viewer.block) return false
    // Past dates: the client-side date filter the view deliberately lacks.
    if (item.workDate < today) return false
    return true
  })

  const graded = browsable.map((item) => ({
    item,
    verdict: fitAgainst(viewer, { date: item.workDate, code: item.shiftCode }, config),
  }))

  const entries = graded
    .filter((entry) => {
      if (entry.item.workDate < filters.range.from || entry.item.workDate > filters.range.to) {
        return false
      }
      if (filters.code !== 'ALL' && entry.item.shiftCode !== filters.code) return false
      if (filters.hideWarnings && entry.verdict.kind === 'WARN') return false
      return true
    })
    .sort((a, b) => {
      const band = bandRank(a.verdict) - bandRank(b.verdict)
      if (band !== 0) return band
      if (a.item.workDate !== b.item.workDate) return a.item.workDate < b.item.workDate ? -1 : 1
      return a.item.advertisementId < b.item.advertisementId ? -1 : 1
    })

  return {
    entries,
    noAds: items.length === 0,
    hiddenCount: items.length - entries.length,
  }
}
